import { useNavigate, useSearch } from "@tanstack/react-router";
import { api } from "@signet/web-kit";

/* ──────────────────────────────────────────────────────────────────────────
   What the Research pages read from /api/m/research, typed once.

   The shapes mirror modules/research/server/routes.ts. The web half never
   imports the server half, so these are written out by hand; when a route
   changes shape, this file is the one place the pages learn about it.
   ────────────────────────────────────────────────────────────────────────── */

export const BASE = "/api/m/research";

export type Within = "24h" | "7d" | "30d" | "6m" | "any";
export type Sort = "multiplier" | "views" | "newest";

export type Outlier = {
  id: string;
  url: string;
  views: number;
  multiplier: number;
  normal: number;
  postedAt: string;
  caption: string;
  cover: string | null;
  /** Streams the Reel for the hover preview; null once Instagram's link has expired. */
  video: string | null;
  /** Set on the Removed view: when it was taken off Viral and by whom. */
  removed: { at: string; by: string | null } | null;
  /** The niches this Reel shows in on Viral Vault (its creator's, less any it was taken out of). */
  niches: string[];
  /** The niches it was taken out of, the latest of those removals, and by whom. */
  removedFrom: { niches: string[]; at: string; by: string | null } | null;
  /** niches: every niche the creator is in that this person may see. */
  creator: { id: string; handle: string; photo: string | null; niches: string[] };
};

export type OutlierPage = { outliers: Outlier[]; next: number | null; removedCount: number | null; setup: { creators: number } | null };

export type NicheRef = { id: string; name: string; color?: string | null };
/** A page of the niche check: Reels still to check, newest first, and on the first page how many there are. */
export type CheckPage = { outliers: Outlier[]; next: string | null; total: number | null };
export type Niche = NicheRef & { creators: number };

export type CreatorProblem = "private" | "not_found" | "failing";

export type Creator = {
  id: string;
  handle: string;
  name: string | null;
  niches: string[];
  /** The starred niche, listed first wherever the creator's niches label a video. */
  mainNiche: string | null;
  normal: number | null;
  reels: number | null;
  lastChecked: string | null;
  problem: CreatorProblem | null;
  problemText: string | null;
  paused: boolean;
  demo: boolean;
  photo: string | null;
  /** Below the floor or gone quiet: scanning stopped until someone keeps or removes them. */
  flagged: { at: string; reason: "floor" | "inactive"; average: number; reels: number; lastPost: string | null } | null;
  kept: boolean;
  /** In the Graveyard: what their last final check saw, and when the next one runs (every three months, or on Rescan). */
  graveyard: { at: string; average: number; reels: number; checkedAt: string | null; nextCheck: string | null } | null;
  /** When the creator was added, and by whom when that is known. */
  added: { at: string; by: string | null };
};

export type CreatorsPage = { creators: Creator[]; next: number | null; total: number; niches: NicheRef[]; adders: { id: string; name: string }[]; flaggedCount: number; inactiveCount: number; graveyardCount: number;
  floor: { minAverage: number; reels: number; atLeast: number; inactiveDays: number };
};

export type Source = {
  configured: "env" | "stored" | "missing";
  balance: { requests: number | null; amount: number | null; currency: string | null } | null;
  balanceError: string | null;
  usage: {
    today: number;
    month: number;
    last30: { day: string; requests: number }[];
    /** The last 30 days by the feature that spent them; `unsplit` is from before the split existed. */
    split: { spenders: { key: string; label: string; requests: number }[]; unsplit: number };
  };
  state: { problem: string | null; problemAt: string | null; pausedUntil: string | null };
  /** `priceFromBalance`: the price is HikerAPI's real rate, read off the balance, not the configured one. */
  budget: { dailyRequests: number; pricePerRequest: number; priceFromBalance?: boolean };
  plan: { perDay: number; creators: number; hot: number; active: number; quiet: number; backfilling: number };
  waiting: number;
};


/* ── query keys ─────────────────────────────────────────────────────────── */

export const keys = {
  all: ["research"] as const,
  outliers: (filters: Record<string, unknown>) => ["research", "outliers", filters] as const,
  outliersAll: ["research", "outliers"] as const,
  outlierNiches: ["research", "outlier-niches"] as const,
  creators: (filters: Record<string, unknown>) => ["research", "creators", filters] as const,
  creatorsAll: ["research", "creators"] as const,
  niches: ["research", "niches"] as const,
  source: ["research", "source"] as const,
  check: ["research", "niche-check"] as const,
};

/* ── requests ──────────────────────────────────────────────────────────── */

export function fetchOutliers(filters: { niches?: string; creator?: string; min?: string; within?: string; sort?: string; removed?: string }, offset: number) {
  return api<OutlierPage>(`${BASE}/outliers${qs({ ...filters, offset: offset ? String(offset) : undefined })}`);
}
export const removeOutlier = (id: string) => api(`${BASE}/outliers/${encodeURIComponent(id)}/remove`, { method: "POST" });
export const restoreOutlier = (id: string) => api(`${BASE}/outliers/${encodeURIComponent(id)}/restore`, { method: "POST" });
const nichePath = (id: string, niche: string) => `${BASE}/outliers/${encodeURIComponent(id)}/niches/${encodeURIComponent(niche)}`;
export const removeOutlierFromNiche = (id: string, niche: string) => api(`${nichePath(id, niche)}/remove`, { method: "POST" });
export const restoreOutlierToNiche = (id: string, niche: string) => api(`${nichePath(id, niche)}/restore`, { method: "POST" });
const creatorNichePath = (creator: string, niche: string) => `${BASE}/creators/${encodeURIComponent(creator)}/niches/${encodeURIComponent(niche)}`;
export const removeCreatorFromNiche = (creator: string, niche: string) => api(creatorNichePath(creator, niche), { method: "DELETE" });
/** Stops watching a creator altogether: their Reels and outliers go with them. */
export const removeCreator = (creator: string) => api(`${BASE}/creators/${encodeURIComponent(creator)}`, { method: "DELETE" });
/** The server's cap on one batch (BULK_DELETE_MAX in routes.ts): one page of the Creators list. */
export const BULK_DELETE_MAX = 500;
/** Several creators in one request, each removed exactly as removeCreator does; ids already gone come back in skipped. */
export const removeCreators = (ids: string[]) => api<{ deleted: string[]; skipped: string[] }>(`${BASE}/creators/delete`, { method: "POST", body: { ids } });
export const addCreatorToNiche = (creator: string, niche: string) => api(creatorNichePath(creator, niche), { method: "POST" });
export const fetchOutlierNiches = () => api<{ niches: NicheRef[] }>(`${BASE}/outliers/niches`).then((r) => r.niches);
export const fetchNiches = () => api<{ niches: Niche[] }>(`${BASE}/niches`).then((r) => r.niches);
export const fetchCreators = (filters: { niche?: string; q?: string; flagged?: string; by?: string; sort?: string }, offset = 0) =>
  api<CreatorsPage>(`${BASE}/creators${qs({ ...filters, offset: offset ? String(offset) : undefined })}`);
export const fetchSource = () => api<Source>(`${BASE}/source`);
export const fetchCheckPage = (after: string | null) => api<CheckPage>(`${BASE}/niche-check${qs({ after: after ?? undefined })}`);
export const markReelChecked = (id: string) => api<{ checked: number }>(`${BASE}/niche-check/${encodeURIComponent(id)}/done`, { method: "POST" });

export function qs(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter((entry): entry is [string, string] => Boolean(entry[1]));
  return entries.length ? `?${new URLSearchParams(entries).toString()}` : "";
}

/* ── search params ──────────────────────────────────────────────────────── */

/** A search param that is a non-empty string, or nothing. */
export function str(value: unknown): string | undefined {
  if (typeof value === "number") return String(value);
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

/**
 * Filters live in the URL, so a link to "Fitness outliers this week" is a
 * link and the back button undoes a filter. Empty values are dropped rather
 * than written as `?niches=` so the address stays readable.
 */
export function useSearchState<T extends Record<string, string | undefined>>() {
  // The registered router's types only know the core routes, and a module
  // page reads its own search loosely.
  const search = useSearch({ strict: false } as never) as T;
  const navigate = useNavigate();
  const set = (patch: Partial<T>, options: { replace?: boolean } = {}) =>
    navigate({
      to: ".",
      search: ((prev: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...prev, ...patch };
        for (const key of Object.keys(next)) if (next[key] === undefined || next[key] === "") delete next[key];
        return next;
      }) as never,
      replace: options.replace ?? false,
    });
  return [search, set] as const;
}

/* ── numbers ───────────────────────────────────────────────────────────── */

/** 3.2×, one decimal always, so a column of them lines up. */
export const times = (multiplier: number) => `${tenths(multiplier).toFixed(1)}×`;

/** Down to one decimal, with a nudge so 2.3 stored as 2.2999999 still reads 2.3. */
const tenths = (n: number) => Math.floor(n * 10 + 1e-9) / 10;

/**
 * The colour step for a multiplier. Floor to one decimal first, so the badge
 * never reads 2.0× in the colour of the step above it.
 */
export function heat(multiplier: number): 1 | 2 | 3 | 4 | 5 {
  const m = tenths(multiplier);
  if (m >= 10) return 5;
  if (m >= 5) return 4;
  if (m >= 3) return 3;
  if (m >= 2) return 2;
  return 1;
}

/** "10k", "1.2M", or a plain number, as people write views. */
export function parseViews(text: string): number | null {
  const match = /^\s*([\d.,]+)\s*([kmb])?\s*$/i.exec(text);
  if (!match) return null;
  const n = Number(match[1]!.replace(/,/g, ""));
  if (!Number.isFinite(n)) return null;
  const k = { k: 1e3, m: 1e6, b: 1e9 }[(match[2] ?? "").toLowerCase() as "k" | "m" | "b"] ?? 1;
  return Math.round(n * k);
}

export const instagramProfile = (handle: string) => `https://www.instagram.com/${encodeURIComponent(handle)}/`;
